// src/pages/ReportPage.jsx

import React, { useState, useEffect, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { fetchReport, renderStrapiRichText } from '../api/strapi';
import Footer from '../components/Footer';
import AuthForm from '../components/AuthForm'; // For actual download
import NavNewsletterSignup from '../components/NavNewsletterSignup'; // For "Coming Soon" signup
import AudioPlayer from '../components/AudioPlayer'; // <<< 1. IMPORT THE NEW COMPONENT
import './ReportPage.css'; // Your page-specific styles

const reportModules = require.context(
  '../customReports',
  false,
  /\.(js|jsx)$/
);

export default function ReportPage() {
  const { slug } = useParams();
  
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [DynamicCustomReportComponent, setDynamicCustomReportComponent] = useState(null);
  const [dynamicCustomFileExists, setDynamicCustomFileExists] = useState(false);

  // Effect for checking and preparing slug-specific custom component
  useEffect(() => {
    const moduleKeys = reportModules.keys();
    const normalizedSlug = slug?.trim();
    const expectedFileName = `./${normalizedSlug}.js`;
    const expectedFileNameJsx = `./${normalizedSlug}.jsx`;
    const matchedKey = moduleKeys.find(
      (key) => key === expectedFileName || key === expectedFileNameJsx
    );

    if (matchedKey) {
      setDynamicCustomFileExists(true);
      const customModule = reportModules(matchedKey);
      const CustomReportComponent = customModule.default || customModule;
      setDynamicCustomReportComponent(() => CustomReportComponent);
    } else {
      setDynamicCustomFileExists(false);
      setDynamicCustomReportComponent(null);
    }
  }, [slug]);

  // Effect for fetching the main report data
  useEffect(() => {
    if (slug) {
      setIsLoading(true);
      setError(null);
      setReport(null);
      fetchReport(slug)
        .then((fetchedReportData) => {
          if (!fetchedReportData) {
            setError('Report not found from API.');
          } else {
            setReport(fetchedReportData);
          }
        })
        .catch((e) => {
          console.error("ReportPage: Fetch report error caught:", e);
          setError(e.message || 'Failed to load report data.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [slug]);

  if (isLoading) {
    return <div className="page-container"><p>Loading report details…</p></div>;
  }
  if (error) {
    return <div className="page-container"><p className="error">Error loading report: {error}</p></div>;
  }
  if (!report) {
    return <div className="page-container"><p>Report data is unavailable.</p></div>;
  }

  const shouldUseCustomCodeFile = report.useCustomCode === true;
  const isReportComingSoon = report.isComingSoon === true;

  const reportIdForTracking = report.id;
  const reportPdfFilename = report.pdfFileName || `${report.slug}-report.pdf`;

  return (
    <article className="report-detail-page">
      <div className="hero-wrapper">
        {report.coverUrl && (
          <img src={report.coverUrl} alt={report.headline || 'Report cover'} className="hero" />
        )}
        <div className="overlay">
          <h1>{report.headline}</h1>
          {report.summary && <div className="summary">{report.summary}</div>}
        </div>
      </div>

      {/* --- VVV 2. ADD THE AUDIO PLAYER SECTION HERE VVV --- */}
      {/* Conditionally render the AudioPlayer if a URL exists */}
      <div className="page-container audio-player-section">
        <AudioPlayer 
          src={report.audioRecordingUrl} 
          title={report.headline}
        />
      </div>
      {/* --- ^^^ END OF AUDIO PLAYER SECTION ^^^ --- */}

      {/* Main Content Area */}
      <div className="report-body-content page-container">
        {shouldUseCustomCodeFile && dynamicCustomFileExists && DynamicCustomReportComponent ? (
          <Suspense fallback={<p>Loading custom report module…</p>}>
            <DynamicCustomReportComponent report={report} />
          </Suspense>
        ) : shouldUseCustomCodeFile && !dynamicCustomFileExists ? (
          <div className="report-content">
            <p>This report (slug: "<strong>{slug}</strong>") is set to use a custom display, but the required custom code file was not found.</p>
            {report.main && report.main.length > 0 && (
                <>
                  <p style={{ marginTop: '20px', fontStyle: 'italic' }}>Displaying standard content instead:</p>
                  {renderStrapiRichText(report.main)}
                </>
            )}
          </div>
        ) : (
          report.main && report.main.length > 0 ? (
            <div className="report-content">
              {renderStrapiRichText(report.main)}
            </div>
          ) : (
            !shouldUseCustomCodeFile && <p>No main content available for this report.</p>
          )
        )}
      </div>

      {/* Download Section OR Coming Soon Signup Section */}
      <section id="report-download" className="report-actions-section page-container">
        {isReportComingSoon ? (
          <div className="coming-soon-signup-wrapper">
            <h2>Report coming soon</h2>
            <p className="subline">Sign up to our newsletter and we will let you know when it's ready to view.</p>
            <NavNewsletterSignup onSignupSuccess={() => {
              console.log('Coming soon signup successful from ReportPage itself.');
            }} />
          </div>
        ) : (
          report.pdfUrl ? (
            <AuthForm
              reportSlug={report.slug}
              reportId={reportIdForTracking}
              reportFilename={reportPdfFilename}
            />
          ) : (
            <p>This report is currently not available for download. Please check back later.</p>
          )
        )}
      </section>

      <Footer />
    </article>
  );
}
